<script lang="ts">
  import type { Progress, TestResult } from '@mesurio/engine';
  import { api, type PublicServer, type Whoami } from '../lib/api';
  import { pickServer, runInWorker, type RunHandle } from '../lib/runner';
  import { addHistory, pref } from '../lib/history';
  import { t } from '../lib/i18n';
  import { nf } from '../lib/format';
  import Dial from '../components/Dial.svelte';
  import LiveChart from '../components/LiveChart.svelte';
  import ServerPicker from '../components/ServerPicker.svelte';
  import SharePanel from '../components/SharePanel.svelte';
  import ResultPanel from '../components/ResultPanel.svelte';

  type State = 'idle' | 'selecting' | 'running' | 'done' | 'error';
  let servers = $state<PublicServer[]>([]);
  let who = $state<Whoami | null>(null);
  let loadError = $state('');
  let selected = $state(pref.get('serveur', 'auto'));
  let connType = $state(pref.get('connexion', ''));
  let contribute = $state(pref.get('contribuer', '1') === '1');
  let useLocation = $state(false);
  let multi = $state(false);
  let multiIds = $state<string[]>([]);

  let status = $state<State>('idle');
  let phase = $state<Progress['phase']>('session');
  let progress = $state(0);
  let live = $state(0);
  let rtt = $state<number | null>(null);
  let downSamples = $state<number[]>([]);
  let upSamples = $state<number[]>([]);
  let result = $state<TestResult | null>(null);
  let used = $state<PublicServer | null>(null);
  let errorMsg = $state('');
  let share = $state<{ share_url: string; share_id: string } | null>(null);
  let announce = $state('');
  let multiResults = $state<{ server: PublicServer; result: TestResult | null; error?: string }[]>([]);
  let handle: RunHandle | null = null;

  $effect(() => {
    api<{ servers: PublicServer[] }>('/v1/servers')
      .then((r) => (servers = r.servers))
      .catch((e) => (loadError = 'Liste des serveurs indisponible : ' + e.message));
    api<Whoami>('/v1/whoami').then((w) => (who = w)).catch(() => {});
  });
  $effect(() => pref.set('serveur', selected));
  $effect(() => pref.set('connexion', connType));
  $effect(() => pref.set('contribuer', contribute ? '1' : '0'));

  // Un onglet en arrière-plan est ralenti par le navigateur : le test serait faussé.
  $effect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden' && status === 'running') {
        handle?.abort();
        errorMsg = 'Test interrompu : l’onglet est passé en arrière-plan, ce qui fausse la mesure. Relancez-le en gardant la page visible.';
      }
    };
    document.addEventListener('visibilitychange', onHide);
    return () => document.removeEventListener('visibilitychange', onHide);
  });

  function onProgress(p: Progress) {
    if (p.phase !== phase) announce = t(`phase.${p.phase}` as 'phase.latence');
    phase = p.phase;
    progress = p.progress;
    if (p.rtt_ms !== undefined) rtt = p.rtt_ms;
    if (p.mbps !== undefined) {
      if (p.phase === 'descendant') downSamples = [...downSamples, p.mbps];
      if (p.phase === 'montant') upSamples = [...upSamples, p.mbps];
      // Affichage lissé sur 500 ms (le résultat final est calculé sur la phase stable, voir la méthodologie).
      const s = (p.phase === 'montant' ? upSamples : downSamples).slice(-5);
      live = Math.round((s.reduce((a, b) => a + b, 0) / s.length) * 10) / 10;
    }
  }

  async function position(): Promise<{ lat: number; lon: number } | undefined> {
    if (!useLocation || !navigator.geolocation) return undefined;
    return new Promise((res) =>
      navigator.geolocation.getCurrentPosition(
        (p) => res({ lat: Math.round(p.coords.latitude * 100) / 100, lon: Math.round(p.coords.longitude * 100) / 100 }),
        () => res(undefined),
        { maximumAge: 600000, timeout: 8000, enableHighAccuracy: false },
      ),
    );
  }

  async function runOne(server: PublicServer): Promise<TestResult> {
    used = server;
    downSamples = [];
    upSamples = [];
    live = 0;
    rtt = null;
    phase = 'session';
    progress = 0;
    handle = runInWorker(server.url, onProgress);
    return handle.done;
  }

  async function submit(server: PublicServer, r: TestResult) {
    const entry = {
      at: new Date().toISOString(),
      server: server.id,
      serverName: `${server.name} (${server.city})`,
      down: r.download?.mbps ?? null,
      up: r.upload?.mbps ?? null,
      latency: r.idle_latency.median_ms,
      jitter: r.idle_latency.jitter_ms,
      grade: r.bufferbloat_grade,
      connectionType: connType,
    };
    if (contribute && !r.invalid) {
      try {
        const s = await api<{ share_url: string; share_id: string; delete_token: string }>('/v1/results', {
          body: { client: 'web', connection_type: connType, location: await position(), result: r },
        });
        share = s;
        addHistory({ ...entry, shareId: s.share_id, deleteToken: s.delete_token });
        return;
      } catch (e) {
        r.warnings.push('Résultat non enregistré : ' + (e as Error).message);
      }
    }
    addHistory(entry);
  }

  async function start() {
    errorMsg = '';
    result = null;
    share = null;
    multiResults = [];
    if (!servers.length) {
      errorMsg = 'Aucun serveur disponible pour le moment.';
      return;
    }
    try {
      if (multi) {
        status = 'running';
        const list = servers.filter((s) => multiIds.includes(s.id));
        for (const s of list) {
          try {
            const r = await runOne(s);
            multiResults = [...multiResults, { server: s, result: r }];
            await submit(s, r);
          } catch (e) {
            multiResults = [...multiResults, { server: s, result: null, error: (e as Error).message }];
          }
        }
        status = 'done';
        return;
      }
      let server = servers.find((s) => s.id === selected) ?? null;
      if (!server) {
        status = 'selecting';
        announce = 'Recherche du serveur le plus réactif…';
        server = await pickServer(servers);
        if (!server) throw new Error('Aucun serveur ne répond. Vérifiez votre connexion ou réessayez plus tard.');
      }
      status = 'running';
      const r = await runOne(server);
      result = r;
      status = 'done';
      announce = `Test terminé : ${nf(r.download?.mbps)} Mbit/s en descendant, ${nf(r.upload?.mbps)} Mbit/s en montant.`;
      await submit(server, r);
      result = { ...r };
    } catch (e) {
      status = 'error';
      if (!errorMsg) errorMsg = (e as Error).message;
      announce = errorMsg;
    } finally {
      handle = null;
    }
  }


  const running = $derived(status === 'running' || status === 'selecting');
  let picker: ServerPicker;
  let ping = $state<number | null>(null);
  let downFinal = $state<number | null>(null);
  let upFinal = $state<number | null>(null);
  $effect(() => {
    if (result) {
      ping = result.idle_latency.median_ms;
      downFinal = result.download?.mbps ?? null;
      upFinal = result.upload?.mbps ?? null;
    }
  });
  $effect(() => {
    if (status === 'running' && phase === 'latence' && rtt !== null) ping = rtt;
    if (status === 'running' && phase === 'descendant') downFinal = live;
    if (status === 'running' && phase === 'montant') upFinal = live;
  });
  function begin() {
    ping = downFinal = upFinal = null;
    start();
  }
  const current = $derived(servers.find((x) => x.id === selected) ?? null);
  const dialMode = $derived<'idle' | 'busy' | 'running'>(
    status === 'running' && (phase === 'descendant' || phase === 'montant') ? 'running' : running ? 'busy' : 'idle',
  );
  const dialPhase = $derived(
    status === 'selecting' ? 'Choix du serveur…' : phase === 'session' ? 'Connexion…' : phase === 'latence' ? 'Mesure de la latence…' : phase === 'descendant' ? 'Descendant' : phase === 'montant' ? 'Montant' : '',
  );
  const fmt = (v: number | null, d?: number) => (v === null ? '—' : nf(v, d));
  const conns = ['fibre', 'cable', 'dsl', '4g', '5g', 'satellite', 'wifi-public', 'autre'] as const;
</script>

<div class="test">
  <header class="hero">
    <p class="eyebrow"><span class="live-dot" aria-hidden="true"></span> Réseau de mesure indépendant · {servers.length} serveur{servers.length > 1 ? 's' : ''} en ligne</p>
    <h1><span class="gradient-text">{t('test.title')}</span></h1>
  </header>

  <dl class="strip" aria-label="Mesures">
    <div class:active={running && phase === 'latence'}>
      <dt><svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M4 12h3l2-5 4 10 2-5h5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg> Latence</dt>
      <dd>{fmt(ping, 1)}<span class="u">ms</span></dd>
    </div>
    <div class:active={running && phase === 'descendant'} style="--c: var(--down)">
      <dt><svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M12 4v15m-6-6 6 6 6-6" fill="none" stroke="var(--down)" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" /></svg> Descendant</dt>
      <dd>{fmt(downFinal)}<span class="u">Mbit/s</span></dd>
    </div>
    <div class:active={running && phase === 'montant'} style="--c: var(--up)">
      <dt><svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M12 20V5m-6 6 6-6 6 6" fill="none" stroke="var(--up)" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" /></svg> Montant</dt>
      <dd>{fmt(upFinal)}<span class="u">Mbit/s</span></dd>
    </div>
  </dl>

  <div class="stage">
    <Dial
      mode={dialMode}
      value={live}
      phase={dialPhase}
      direction={phase === 'montant' ? 'up' : phase === 'descendant' ? 'down' : 'ping'}
      label={status === 'done' ? 'Relancer' : 'Lancer'}
      onstart={begin}
    />
    {#if running}
      <div class="progress" role="progressbar" aria-label="Avancement du test" aria-valuemin="0" aria-valuemax="100" aria-valuenow={Math.round(progress * 100)}>
        <span style="width: {progress * 100}%"></span>
      </div>
      <button class="cancel" onclick={() => handle?.abort()}>{t('test.cancel')}</button>
    {/if}
    {#if (running || result) && (downSamples.length || result)}
      <div class="chart"><LiveChart down={result?.download?.samples_mbps ?? downSamples} up={result?.upload?.samples_mbps ?? upSamples} /></div>
    {/if}
    <p class="sr-only" aria-live="polite">{announce}</p>
  </div>

  {#if loadError}<p class="notice bad" role="alert">{loadError}</p>{/if}
  {#if errorMsg}<p class="notice bad" role="alert">{errorMsg}</p>{/if}

  <div class="rows">
    <div class="row-item">
      <span class="ico" aria-hidden="true"><svg viewBox="0 0 24 24" width="22" height="22"><circle cx="12" cy="8.5" r="3.6" fill="none" stroke="currentColor" stroke-width="1.6" /><path d="M5 19.5c1.2-3.4 4-5 7-5s5.8 1.6 7 5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" /></svg></span>
      <span class="txt">
        <strong>{who?.operator ?? 'Votre connexion'}</strong>
        <span class="sub">{who ? `${who.ip} · IPv${who.ip_version}${who.commune ? ' · ' + who.commune : ''}` : 'Détection…'}</span>
      </span>
    </div>
    <div class="row-item">
      <span class="ico" aria-hidden="true"><svg viewBox="0 0 24 24" width="22" height="22"><rect x="4" y="4" width="16" height="6.5" rx="2" fill="none" stroke="currentColor" stroke-width="1.6" /><rect x="4" y="13.5" width="16" height="6.5" rx="2" fill="none" stroke="currentColor" stroke-width="1.6" /><circle cx="8" cy="7.25" r="1" fill="currentColor" /><circle cx="8" cy="16.75" r="1" fill="currentColor" /></svg></span>
      <span class="txt">
        <strong>{current ? current.sponsor : used && status === 'done' ? used.sponsor : 'Automatique'}</strong>
        <span class="sub">
          {#if current}{current.city} · {current.distance_km.toLocaleString('fr-FR')} km
          {:else if used && status === 'done'}{used.city} · choisi automatiquement
          {:else if servers.length}{servers[0].sponsor} — {servers[0].city}{servers.length > 1 ? ` · le plus réactif parmi ${servers.length} serveurs` : ''}
          {:else}Aucun serveur disponible{/if}
        </span>
      </span>
      <button class="link" onclick={() => picker.show()} disabled={running || multi} aria-label="Changer de serveur">Changer</button>
    </div>
    <div class="row-item">
      <span class="ico" aria-hidden="true"><svg viewBox="0 0 24 24" width="22" height="22"><path d="M3 9.5a13 13 0 0 1 18 0M6 13a8.5 8.5 0 0 1 12 0M9 16.4a4 4 0 0 1 6 0" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" /><circle cx="12" cy="19.4" r="1.2" fill="currentColor" /></svg></span>
      <label class="txt" for="connexion"><strong class="nowrap">Type d’accès</strong><span class="sub hide-sm">Affine les statistiques publiques</span></label>
      <select id="connexion" bind:value={connType} disabled={running}>
        <option value="">Non renseigné</option>
        {#each conns as c}<option value={c}>{t(`conn.${c}`)}</option>{/each}
      </select>
    </div>
  </div>

  <details class="options">
    <summary>Options</summary>
    <label class="inline"><input type="checkbox" bind:checked={contribute} disabled={running} /> {t('test.contribute')}</label>
    <p class="field-help">Seuls sont conservés le résultat, l’opérateur, la commune et un préfixe d’adresse tronqué. <a href="/confidentialite">En savoir plus</a></p>
    <label class="inline"><input type="checkbox" bind:checked={useLocation} disabled={running || !contribute} /> {t('test.location')}</label>
    <label class="inline"><input type="checkbox" bind:checked={multi} disabled={running} /> {t('test.multi')}</label>
    {#if multi}
      <fieldset>
        <legend class="small">Serveurs à comparer (tests successifs)</legend>
        {#each servers.slice(0, 12) as s}
          <label class="inline"><input type="checkbox" value={s.id} bind:group={multiIds} /> {s.city} — {s.sponsor}</label>
        {/each}
      </fieldset>
    {/if}
  </details>
</div>

<ServerPicker bind:this={picker} {servers} bind:selected />

{#if result && !multi}
  <div class="after">
    <ResultPanel
      down={result.download?.mbps}
      up={result.upload?.mbps}
      latency={result.idle_latency.median_ms}
      jitter={result.idle_latency.jitter_ms}
      loadedDown={result.download?.loaded_latency.median_ms}
      loadedUp={result.upload?.loaded_latency.median_ms}
      grade={result.bufferbloat_grade}
      serverName={used ? `${used.sponsor}, ${used.city}` : ''}
      warnings={result.warnings}
    />
    {#if share}<SharePanel shareId={share.share_id} />{/if}
  </div>
{/if}

{#if multiResults.length}
  <section class="card after" aria-labelledby="titre-multi">
    <h2 id="titre-multi">Comparaison des serveurs</h2>
    <div class="table-wrap">
      <table>
        <thead><tr><th scope="col">Serveur</th><th scope="col">Descendant</th><th scope="col">Montant</th><th scope="col">Latence</th><th scope="col">Réactivité</th></tr></thead>
        <tbody>
          {#each multiResults as m}
            <tr>
              <th scope="row">{m.server.city} — {m.server.sponsor}</th>
              {#if m.result}
                <td>{nf(m.result.download?.mbps)} Mbit/s</td><td>{nf(m.result.upload?.mbps)} Mbit/s</td><td>{nf(m.result.idle_latency.median_ms, 1)} ms</td><td>{m.result.bufferbloat_grade}</td>
              {:else}
                <td colspan="4" class="error">{m.error}</td>
              {/if}
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  </section>
{/if}

<style>
  .test { max-width: 680px; margin: 0 auto; }
  .hero { text-align: center; margin: 6px 0 14px; }
  .hero h1 { font-size: clamp(1.7rem, 5vw, 2.6rem); margin: 0.3rem 0 0; }
  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    padding: 0.3rem 0.85rem;
    border-radius: 999px;
    border: 1px solid var(--border);
    background: var(--surface);
    backdrop-filter: blur(10px);
    font-size: 0.82rem;
    color: var(--muted);
  }
  .live-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--ok); box-shadow: 0 0 10px var(--ok); animation: pulse 2s ease-in-out infinite; }

  /* Bandeau des mesures en verre */
  .strip {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    margin: 0;
    padding: 6px;
    gap: 6px;
    border: 1px solid var(--border);
    border-radius: 22px;
    background: var(--surface);
    backdrop-filter: blur(16px);
    box-shadow: var(--shadow);
  }
  .strip div { position: relative; text-align: center; padding: 10px 4px 12px; border-radius: 16px; transition: background 0.3s ease, box-shadow 0.3s ease; }
  .strip div.active { background: color-mix(in oklab, var(--c, var(--ring-a)) 12%, transparent); box-shadow: inset 0 0 0 1px color-mix(in oklab, var(--c, var(--ring-a)) 40%, transparent); }
  .strip div.active::after {
    content: '';
    position: absolute;
    left: 30%;
    right: 30%;
    bottom: 4px;
    height: 3px;
    border-radius: 3px;
    background: var(--c, var(--ring-a));
    box-shadow: 0 0 12px var(--c, var(--ring-a));
  }
  .strip dt { display: inline-flex; align-items: center; gap: 0.3rem; font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--muted); }
  .strip dd {
    margin: 0.2rem 0 0;
    font-family: var(--font-display);
    font-size: clamp(1.5rem, 6.5vw, 2.1rem);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.03em;
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 1.1;
  }
  .strip .u { font-family: var(--font-body); font-size: 0.72rem; font-weight: 500; letter-spacing: 0; color: var(--muted); }

  .stage { display: flex; flex-direction: column; align-items: center; gap: 14px; padding: 22px 0 10px; }
  .progress { width: min(78vw, 340px); height: 6px; background: var(--surface-2); border-radius: 3px; overflow: hidden; }
  .progress span { display: block; height: 100%; background: var(--grad); background-size: 200% 100%; animation: defile 2s linear infinite; box-shadow: 0 0 12px var(--ring-b); transition: width 0.2s; }
  .cancel { border: 0; background: transparent; color: var(--muted); text-decoration: underline; min-height: 36px; backdrop-filter: none; }
  .cancel:hover { background: transparent; color: var(--text); }
  .chart { width: 100%; }

  /* Lignes d'information dans une carte en verre */
  .rows {
    margin-top: 10px;
    border: 1px solid var(--border);
    border-radius: 22px;
    background: var(--surface);
    backdrop-filter: blur(16px);
    box-shadow: var(--shadow);
    overflow: hidden;
  }
  .row-item { display: flex; align-items: center; gap: 14px; padding: 14px 16px; border-bottom: 1px solid var(--border); transition: background 0.2s ease; }
  .row-item:last-child { border-bottom: 0; }
  .row-item:hover { background: var(--surface-2); }
  .ico {
    width: 46px;
    height: 46px;
    flex: none;
    border-radius: 14px;
    display: grid;
    place-items: center;
    color: var(--text);
    background: linear-gradient(var(--surface-solid), var(--surface-solid)) padding-box, var(--grad) border-box;
    border: 1.5px solid transparent;
    box-shadow: 0 0 18px -6px var(--ring-a);
  }
  .txt { flex: 1; min-width: 0; display: flex; flex-direction: column; margin: 0; font-weight: 400; }
  .txt strong { font-size: 1.06rem; font-weight: 650; }
  .sub { color: var(--muted); font-size: 0.9rem; overflow: hidden; text-overflow: ellipsis; }
  .link { border: 1px solid color-mix(in oklab, var(--ring-a) 45%, var(--border)); background: color-mix(in oklab, var(--ring-a) 10%, transparent); color: var(--primary); font-weight: 650; padding: 0.35rem 0.9rem; border-radius: 999px; white-space: nowrap; min-height: 38px; }
  .row-item select { max-width: 46%; }
  .nowrap { white-space: nowrap; }
  .options { margin: 14px 0 0; }
  .options summary { cursor: pointer; color: var(--muted); font-weight: 600; padding: 0.4rem 0.2rem; min-height: 44px; display: flex; align-items: center; }
  .options summary:hover { color: var(--text); }
  fieldset { border: 1px solid var(--border); border-radius: 14px; margin-top: 0.5rem; }
  .after { max-width: 920px; margin: 30px auto 0; }
  .after > :global(* + *) { margin-top: 16px; }
  @keyframes defile { to { background-position: 200% 0; } }
  @keyframes pulse { 50% { opacity: 0.35; transform: scale(0.75); } }
  @media (max-width: 480px) {
    .hide-sm { display: none; }
    .row-item select { max-width: 52%; }
    .eyebrow { font-size: 0.75rem; }
  }
</style>
